import React from "react";
import * as Icons from "lucide-react";
import { INDUSTRIES, APPROACH, TESTIMONIALS, CERT_LOGOS, CLIENTS, ABOUT_IMAGE } from "../data/mock";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, CheckCircle2 } from "lucide-react";

export const IndustriesSection = ({ limit }) => {
  const list = limit ? INDUSTRIES.slice(0, limit) : INDUSTRIES;
  return (
    <section className="section-pad bg-slate-50">
      <div className="lis-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Industries We Serve</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">Deep sector expertise, from shopfloor to boardroom</h2>
            <div className="divider-red mt-4" />
          </div>
          <Link to="/industries" className="text-brand font-semibold text-sm inline-flex items-center gap-2">
            View all industries <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.map((ind) => {
            const Icon = Icons[ind.icon] || Icons.Building2;
            return (
              <div key={ind.name} className="industry-card card-hover">
                <div className="w-10 h-10 rounded-lg bg-red-50 text-brand flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <h3 className="mt-3 font-semibold text-slate-900">{ind.name}</h3>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{ind.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const ApproachSection = () => (
  <section className="section-pad bg-white">
    <div className="lis-container">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4">
          <span className="eyebrow">Our Approach</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">A proven 6-step ISO implementation methodology</h2>
          <div className="divider-red mt-4" />
          <p className="mt-4 text-slate-600">
            Whether you're pursuing ISO 9001, 27001, 14001, 45001 or an integrated management system   our field-tested playbook takes the complexity out of certification.
          </p>
          <Link to="/contact" className="btn-brand mt-6">Talk to a Consultant <ArrowRight size={14} /></Link>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
          {APPROACH.map((a) => {
            const Icon = Icons[a.icon] || Icons.CheckCircle2;
            return (
              <div key={a.step} className="p-5 rounded-xl border border-slate-100 hover:border-brand/40 bg-white card-hover">
                <div className="flex items-center justify-between">
                  <span className="text-4xl font-extrabold text-red-100">{a.step}</span>
                  <Icon size={22} className="text-brand" />
                </div>
                <h4 className="mt-2 font-semibold text-slate-900">{a.title}</h4>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{a.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export const WhyChooseUs = () => (
  <section className="section-pad bg-slate-50">
    <div className="lis-container grid lg:grid-cols-2 gap-12 items-center">
      <div className="relative">
        <img src={ABOUT_IMAGE} alt="LIS team" className="rounded-2xl w-full h-[440px] object-cover shadow-xl" />
        <div className="absolute -bottom-6 -right-6 bg-brand text-white p-6 rounded-2xl shadow-2xl w-56">
          <div className="text-3xl font-extrabold">13+</div>
          <div className="text-sm opacity-90">Years of hands-on ISO consulting experience</div>
        </div>
      </div>
      <div>
        <span className="eyebrow">Why LIINEX INFORMATION SYSTEMS</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">Consultants who have implemented, not just audited</h2>
        <div className="divider-red mt-4" />
        <p className="mt-4 text-slate-600">
          Our team has built management systems inside factories, laboratories, hospitals and technology firms. That means we design ISO systems that people actually use   not shelf-ware.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          {[
            "IRCA & Exemplar Global certified auditors",
            "Sector-specialised consultants (auto, food, pharma, IT)",
            "Ready-to-use toolkits & templates",
            "Bilingual training (English + Hindi)",
            "Fixed-fee, milestone-based engagements",
            "Post-certification surveillance support",
          ].map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const TestimonialsSection = () => (
  <section className="section-pad bg-white">
    <div className="lis-container">
      <div className="max-w-2xl">
        <span className="eyebrow">Client Voices</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">What our clients say about working with LIS</h2>
        <div className="divider-red mt-4" />
      </div>
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="testimonial-card card-hover">
            <Quote size={28} className="text-brand opacity-30" />
            <p className="mt-3 text-slate-700 leading-relaxed">“{t.quote}”</p>
            <div className="mt-5 flex items-center gap-3">
              <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
              <div>
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const AccreditationBar = () => (
  <section className="py-10 bg-slate-900">
    <div className="lis-container">
      <div className="text-center text-slate-400 text-xs uppercase tracking-widest">Certification bodies we work with</div>
      <div className="marquee mt-5">
        <div className="marquee-track">
          {[...CERT_LOGOS, ...CERT_LOGOS].map((c, i) => (
            <div key={i} className="text-white text-2xl font-bold opacity-70 hover:opacity-100 transition-opacity whitespace-nowrap">
              {c}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const ClientsMarquee = () => (
  <section className="py-10 bg-white border-y border-slate-100">
    <div className="lis-container">
      <div className="text-center text-slate-500 text-xs uppercase tracking-widest">Trusted by leading enterprises</div>
      <div className="marquee mt-5">
        <div className="marquee-track">
          {[...CLIENTS, ...CLIENTS].map((c, i) => (
            <div key={i} className="text-slate-400 text-xl font-bold hover:text-brand transition-colors whitespace-nowrap">
              {c}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
